import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import dayjs from 'dayjs';

import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { InvoicePrepaidCandidate, PrepaidType } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Utils } from '@shared/utils/utils';

type AgInvoiceDropdownOption = InvoicePrepaidCandidate & {
  disabled: boolean;
  textToSearch?: string;
};

type AgInvoiceDropdownParams = ICellRendererParams & {
  options: AgInvoiceDropdownOption[];
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgSelectModule, FormsModule, ReactiveFormsModule, TooltipDirective],
  styleUrl: 'ag-invoice-dropdown.component.scss',
  templateUrl: 'ag-invoice-dropdown.component.html',
})
export class AgInvoiceDropdownComponent implements ICellRendererAngularComp {
  readonly launchDarklyService = inject(LaunchDarklyService);
  readonly mainQuery = inject(MainQuery);

  formControl = new FormControl();
  params!: AgInvoiceDropdownParams;
  readonly utils = Utils;

  accrualPeriod(item: InvoicePrepaidCandidate): string {
    return item.accrual_period ? dayjs(item.accrual_period).format('MMMM YYYY') : Utils.zeroHyphen;
  }

  agInit(params: AgInvoiceDropdownParams): void {
    this.params = params;

    if (params.options.length > 0) {
      this.params.options = params.options.map((invoice) => ({
        ...invoice,
        disabled: this.isOptionDisabled(invoice),
        textToSearch: `${invoice.invoice_no} ${invoice.amount_total ? this.totalAmount(invoice) : ''} ${invoice.accrual_period ? this.accrualPeriod(invoice) : ''}`,
      }));
    }

    this.formControl.setValue(params.value);
  }

  getValue(): string {
    return this.formControl.value;
  }

  private isOptionDisabled(item: AgInvoiceDropdownOption): boolean {
    if (!item.id) {
      return false;
    }

    if (this.launchDarklyService.flags$.getValue().journal_entry_ap_manual_clearing) {
      if (this.params.data.prepaid_type === PrepaidType.PREPAID_TYPE_INCREASE) {
        return (
          !item.payment_date ||
          dayjs(item.payment_date).isAfter(
            dayjs(this.mainQuery.getValue().currentOpenMonth).endOf('month')
          )
        );
      }
    }

    return false;
  }

  refresh(): boolean {
    return false;
  }

  search(term: string, item: AgInvoiceDropdownOption) {
    return item.textToSearch?.toLowerCase().includes(term.toLowerCase());
  }

  tooltip(item: AgInvoiceDropdownOption): string {
    return item.disabled ? MessagesConstants.PREPAIDS.INCREASE_ONLY_PAID_INVOICE : '';
  }

  totalAmount(item: InvoicePrepaidCandidate): string {
    return item.amount_total ? Utils.currencyFormatter(item.amount_total) : Utils.zeroHyphen;
  }
}
